import { useState, useEffect } from 'react';
import { usersAPI } from '../lib/api';

const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'User' },
];

const PERMISSION_LEVELS = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
  { value: 'admin', label: 'Admin' },
];

export default function PermissionManager({
  resourceId,
  resourceType, // 'document' or 'folder'
  currentUser,
  onPermissionsChange,
  getRolePermissions,
  setRolePermissions,
  propagateRolePermissions, // Optional - only for folders
  getUserPermissions,
  grantUserPermission,
  revokeUserPermission,
}) {
  const [activeTab, setActiveTab] = useState('roles');
  const [rolePermissions, setRolePermissionsState] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [propagateToChildren, setPropagateToChildren] = useState(false);

  // Form states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPermissionLevel, setSelectedPermissionLevel] = useState('view');

  useEffect(() => {
    loadPermissions();
    loadUsers();
  }, [resourceId]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const [roleRes, userRes] = await Promise.all([
        getRolePermissions(resourceId),
        getUserPermissions(resourceId),
      ]);
      setRolePermissionsState(roleRes.data.allowed_roles);
      setSelectedRoles(roleRes.data.allowed_roles || []);
      setUserPermissions(userRes.data.permissions || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setAllUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSaveRolePermissions = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const permissionData = {
        allowed_roles: selectedRoles.length > 0 ? selectedRoles : null,
      };
      
      // Use propagate endpoint if checkbox is checked and we're on a folder
      if (propagateToChildren && resourceType === 'folder' && propagateRolePermissions) {
        const response = await propagateRolePermissions(resourceId, permissionData);
        setRolePermissionsState(selectedRoles.length > 0 ? selectedRoles : null);
        if (onPermissionsChange) onPermissionsChange();
        alert(`Permissions updated and propagated successfully!\n${response.data.updated_folders} folder(s) and ${response.data.updated_documents} document(s) updated.`);
      } else {
        await setRolePermissions(resourceId, permissionData);
        setRolePermissionsState(selectedRoles.length > 0 ? selectedRoles : null);
        if (onPermissionsChange) onPermissionsChange();
        alert('Role permissions updated successfully');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update role permissions');
    } finally {
      setSaving(false);
      setPropagateToChildren(false);
    }
  };

  const handleGrantUserPermission = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await grantUserPermission(resourceId, {
        user_id: selectedUser,
        permission_level: selectedPermissionLevel,
      });
      await loadPermissions();
      setSelectedUser('');
      if (onPermissionsChange) onPermissionsChange();
      alert('Permission granted successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to grant permission');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeUserPermission = async (userId) => {
    if (!confirm('Are you sure you want to revoke this permission?')) return;

    try {
      setSaving(true);
      setError(null);
      await revokeUserPermission(resourceId, userId);
      await loadPermissions();
      if (onPermissionsChange) onPermissionsChange();
      alert('Permission revoked successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke permission');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500">Loading permissions...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-4 px-6 text-sm font-medium border-b-2 ${
              activeTab === 'roles'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Role-Based Permissions
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-6 text-sm font-medium border-b-2 ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            User-Specific Permissions
          </button>
        </nav>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Role-Based Permissions Tab */}
        {activeTab === 'roles' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Restrict Access by Role
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select which roles can access this {resourceType}. Leave all unchecked to allow all authenticated users.
            </p>

            <div className="space-y-3 mb-6">
              {AVAILABLE_ROLES.map((role) => (
                <label key={role.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">{role.label}</span>
                </label>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Current setting:</strong>{' '}
                {selectedRoles.length === 0
                  ? 'All authenticated users can access this ' + resourceType
                  : `Only ${selectedRoles.join(', ')} roles can access this ${resourceType}`}
              </p>
            </div>

            {/* Propagate option for folders */}
            {resourceType === 'folder' && propagateRolePermissions && (
              <div className="mb-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={propagateToChildren}
                    onChange={(e) => setPropagateToChildren(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <span className="ml-3">
                    <span className="text-sm font-medium text-gray-900">Propagate to all child folders and documents</span>
                    <p className="text-xs text-gray-600 mt-1">
                      This will apply these permissions to all subfolders and documents within this folder. 
                      You can manually override permissions on individual items afterwards.
                    </p>
                  </span>
                </label>
              </div>
            )}

            <button
              onClick={handleSaveRolePermissions}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : propagateToChildren ? 'Save and Propagate Permissions' : 'Save Role Permissions'}
            </button>
          </div>
        )}

        {/* User-Specific Permissions Tab */}
        {activeTab === 'users' && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Grant Access to Specific Users
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Grant individual users access to this {resourceType}, regardless of their role.
            </p>

            {/* Grant Permission Form */}
            <form onSubmit={handleGrantUserPermission} className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select a user</option>
                    {allUsers
                      .filter(u => u.id !== currentUser?.id)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.email})
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permission Level
                  </label>
                  <select
                    value={selectedPermissionLevel}
                    onChange={(e) => setSelectedPermissionLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PERMISSION_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Granting...' : 'Grant Permission'}
                  </button>
                </div>
              </div>
            </form>

            {/* User Permissions List */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-4">
                Current User Permissions
              </h4>
              {userPermissions.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-sm text-gray-500">
                    No user-specific permissions granted yet.
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Use the form above to grant access to individual users
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {userPermissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1">
                        <p className="text-base font-medium text-gray-900">
                          {permission.user_profiles?.full_name || 'Unknown User'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {permission.user_profiles?.email}
                        </p>
                        <span className="inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {permission.permission_level}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRevokeUserPermission(permission.user_id)}
                        disabled={saving}
                        className="ml-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
